"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Cable, Lock, ShieldCheck, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Feature = {
  spec: string;
  title: string;
  description: string;
  statValue: number;
  statSuffix: string;
  statPrefix: string;
  statLabel: string;
  iconKey: string;
};
const features: Feature[] = [{
  spec: "STD.001 / NFPA",
  title: "NFPA 70E Compliance",
  description: "Comprehensive hazard analysis and labeling protocols ensuring full adherence to national electrical safety standards.",
  statValue: 100,
  statSuffix: "%",
  statPrefix: "",
  statLabel: "regulatory alignment",
  iconKey: "fiber"
}, {
  spec: "STD.002 / DATA",
  title: "Precision Incident Energy",
  description: "Advanced calculation of incident energy levels to determine exact PPE requirements for every electrical panel.",
  statValue: 40,
  statSuffix: " cal",
  statPrefix: "<",
  statLabel: "max energy rating",
  iconKey: "acoustic"
}, {
  spec: "STD.003 / SAFETY",
  title: "Durable Hazard Signage",
  description: "Industrial-grade, UV-resistant labels designed to withstand harsh environments and remain legible for years.",
  statValue: 10,
  statSuffix: " yr",
  statPrefix: "",
  statLabel: "durability rating",
  iconKey: "power"
}, {
  spec: "STD.004 / ACCESS",
  title: "Digital Audit Trails",
  description: "Centralized documentation of all labeling updates and maintenance logs for seamless safety inspections.",
  statValue: 256,
  statSuffix: "-bit",
  statPrefix: "",
  statLabel: "encrypted records",
  iconKey: "access"
}];
function CountUp({
  value,
  prefix,
  suffix,
  isVisible
}: {
  value: number;
  prefix: string;
  suffix: string;
  isVisible: boolean;
}) {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;
    const duration = 1400;
    const start = performance.now();
    const isFloat = !Number.isInteger(value);
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = value * eased;
      setDisplay(isFloat ? Math.round(current * 100) / 100 : Math.round(current));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, value]);
  return <span className="font-mono tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>;
}
function FeatureIcon({
  iconKey
}: {
  iconKey: string;
}) {
  const baseClass = "text-primary transition-transform duration-500 group-hover:scale-105";
  if (iconKey === "fiber") {
    return <Cable className={`${baseClass} size-10`} strokeWidth={1.25} />;
  }
  if (iconKey === "acoustic") {
    return <Waves className={`${baseClass} size-10 group-hover:animate-pulse`} strokeWidth={1.25} />;
  }
  if (iconKey === "power") {
    return <ShieldCheck className={`${baseClass} size-10`} strokeWidth={1.25} />;
  }
  return <Lock className={`${baseClass} size-10`} strokeWidth={1.25} />;
}
function FeatureCard({
  feature,
  index
}: {
  feature: Feature;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div data-index={index} ref={ref} className={`group relative flex flex-col justify-between min-h-[320px] rounded-lg border border-border bg-card p-8 sm:p-10 transition-all duration-500 hover:border-primary hover:-translate-y-0.5 overflow-hidden shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: `${index * 80}ms`
  }}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <FeatureIcon iconKey={feature.iconKey} />
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
            0{index + 1}
          </span>
        </div>
        <div className="flex flex-col gap-3">
          <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            {feature.spec}
          </span>
          <h3 className="font-default text-[22px] font-medium leading-tight text-foreground">
            {feature.title}
          </h3>
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {feature.description}
        </p>
        <div className="flex items-baseline gap-2 pt-2">
          <span className="text-2xl font-default font-semibold text-primary">
            <CountUp value={feature.statValue} prefix={feature.statPrefix} suffix={feature.statSuffix} isVisible={isVisible} />
          </span>
          <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            {feature.statLabel}
          </span>
        </div>
      </div>
      <span className="absolute bottom-0 left-0 h-px w-0 bg-primary transition-all duration-500 group-hover:w-full" />
    </div>;
}
export default function ArcFlashLabelingRequirements() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="2353" id="arc-flash-labeling-requirements" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] px-6 md:px-10">
      <div className="mx-auto max-w-7xl">
        <div ref={headerRef} className={`flex flex-col gap-6 max-w-4xl transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs tracking-widest text-primary uppercase">
            [02] / COMPLIANCE
          </span>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-[56px] font-medium leading-[1.05] tracking-tight text-foreground">
            Arc Flash Labeling Requirements
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-[540px]">
            Ensure workplace safety and regulatory compliance with our precision-engineered 
            labeling solutions, designed for clarity and long-term durability.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.slice(0, 2).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i} />)}
          <div className="hidden lg:contents">
            {features.slice(2, 4).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i + 2} />)}
          </div>
        </div>

        <div className="hidden sm:flex lg:hidden items-center gap-6 my-8">
          <Separator className="flex-1 bg-border" />
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
            Continued
          </span>
          <Separator className="flex-1 bg-border" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4 md:gap-5">
          {features.slice(2, 4).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i + 2} />)}
        </div>

        <div className="mt-20 flex justify-center">
          <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-mono text-sm text-foreground hover:text-primary transition-colors">
            <span className="relative">
              View full compliance documentation
              <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
            </span>
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}